/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
/**
 * Based on https://github.com/iamanvesh/mapbox-gl-draw-circle (MIT License), which is abandoned.
 */
import MapboxDraw, {
  DrawCustomMode,
  DrawCustomModeThis,
  constants,
} from '@mapbox/mapbox-gl-draw';
import along from '@turf/along';
import circle from '@turf/circle';
import distance from '@turf/distance';
import { Feature, Polygon, Position, point } from '@turf/helpers';
import { lineString } from '@turf/turf';
import MapHelpers from '../map/map.utils';
import { DrawingCircleFeature } from '../models/drawings.model';
import GeoHelper from './geo-helpers.utils';
import {
  addEndMarkerSourceAndLayer,
  pointFeatureCollection,
  removeLayerIfExists,
  removeSourceIfExists,
} from './mapbox-helper';
import { neatDisplayNumber } from './text-formatting.utils';

const SOURCE_LINE = 'draw-source-radius';
export const SOURCE_SYMBOL = 'draw-source-marker';

export interface DrawCustomModeThisWithContext extends DrawCustomModeThis {
  _ctx?: {
    store: {
      getInitialConfigValue(key: string): boolean;
    };
  };
}

const dragPan = {
  enable(ctx: DrawCustomModeThisWithContext) {
    setTimeout(() => {
      // First check we've got a map and some context.
      if (
        !ctx.map ||
        !ctx.map.dragPan ||
        !ctx._ctx ||
        !ctx._ctx.store ||
        !ctx._ctx.store.getInitialConfigValue
      )
        return;
      // Now check initial state wasn't false (we leave it disabled if so)
      if (!ctx._ctx.store.getInitialConfigValue('dragPan')) return;
      ctx.map.dragPan.enable();
    }, 0);
  },
  disable(ctx: DrawCustomModeThisWithContext) {
    setTimeout(() => {
      if (!ctx.map || !ctx.map.doubleClickZoom) return;
      // Always disable here, as it's necessary in some cases.
      ctx.map.dragPan.disable();
    }, 0);
  },
};

const DragCircleMode: DrawCustomMode = { ...MapboxDraw.modes.draw_polygon };

interface State {
  polygon: DrawingCircleFeature & {
    incomingCoords(coords: Position[][]): void;
    toGeoJSON(): Feature<Polygon, { id: string }>;
    isValid(): boolean;
  };
}

export function lineStringFeature(coordinates: Position[]): GeoJSON.Feature {
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates,
    },
  };
}

export const addLineSourceAndLayer = () => {
  MapHelpers.addSource(SOURCE_LINE, {
    type: 'geojson',
    data: lineStringFeature([]),
  });
  MapHelpers.addLayer({
    id: SOURCE_LINE,
    type: 'line',
    source: SOURCE_LINE,
    paint: {
      'line-color': '#fbb03b',
      'line-width': 3,
    },
  });
};

DragCircleMode.onSetup = function onSetup() {
  if (!this.map.getSource(SOURCE_LINE)) {
    addLineSourceAndLayer();
  }
  if (!this.map.getSource(SOURCE_SYMBOL)) {
    addEndMarkerSourceAndLayer(SOURCE_SYMBOL);
  }
  const polygon = this.newFeature({
    type: constants.geojsonTypes.FEATURE,
    properties: {
      isCircle: true,
      center: [],
    },
    geometry: {
      type: constants.geojsonTypes.POLYGON,
      coordinates: [
        [
          [0, 0],
          [0, 0],
        ],
      ],
    },
  } as DrawingCircleFeature);

  this.addFeature(polygon);

  this.clearSelectedFeatures();
  MapboxDraw.lib.doubleClickZoom.disable(this);
  dragPan.disable(this);
  this.setActionableState({
    trash: true,
    combineFeatures: false,
    uncombineFeatures: false,
  });

  return {
    polygon,
    currentVertexPosition: 0,
  };
};

DragCircleMode.onMouseDown = function onMouseDown(state: State, e) {
  const currentCenter = state.polygon.properties.center;
  if (currentCenter.length === 0) {
    state.polygon.properties.center = [e.lngLat.lng, e.lngLat.lat];
  }
};

DragCircleMode.onTouchStart = function onTouchStart(state: State, e) {
  const currentCenter = state.polygon.properties.center;
  if (currentCenter.length === 0) {
    state.polygon.properties.center = [e.lngLat.lng, e.lngLat.lat];
  }
};

DragCircleMode.onDrag = function onDrag(state: State, e) {
  const { center } = state.polygon.properties;
  if (center.length > 0) {
    const distanceInNm = Math.min(
      distance(point(center), point([e.lngLat.lng, e.lngLat.lat]), {
        units: 'nauticalmiles',
      }),
      6000
    );
    // The line goes towards the mouse cursor but stops at distanceInKm
    const maxPoint = along(
      lineString([center, [e.lngLat.lng, e.lngLat.lat]]),
      distanceInNm,
      { units: 'nauticalmiles' }
    );

    GeoHelper.setMapboxGeoJSONSourceData(
      SOURCE_LINE,
      lineStringFeature([center, maxPoint.geometry.coordinates])
    );

    GeoHelper.setMapboxGeoJSONSourceData(
      SOURCE_SYMBOL,
      pointFeatureCollection(
        [maxPoint.geometry.coordinates],
        [`${neatDisplayNumber(distanceInNm)} NM`]
      )
    );

    const circleFeature = circle(center, distanceInNm, {
      steps: 64,
      units: 'nauticalmiles',
    });
    state.polygon.incomingCoords(circleFeature.geometry.coordinates);

    state.polygon.properties.radiusInNm = distanceInNm;
  }
};

DragCircleMode.onMouseMove = DragCircleMode.onDrag;

DragCircleMode.onMouseUp = function onMouseUp(state: State) {
  return this.changeMode(constants.modes.SIMPLE_SELECT, {
    featureIds: [state.polygon.id],
  });
};

DragCircleMode.onTouchEnd = function onTouchEnd(state: State) {
  return this.changeMode(constants.modes.SIMPLE_SELECT, {
    featureIds: [state.polygon.id],
  });
};

DragCircleMode.onKeyUp = function onKeyUp(state: State, e) {
  if (e.key === 'Escape') {
    this.deleteFeature(state.polygon.id as string, { silent: true });
    state.polygon.isValid = () => false;
    this.changeMode(constants.modes.SIMPLE_SELECT);
  } else if (e.key === 'Enter') {
    this.changeMode(constants.modes.SIMPLE_SELECT, {
      featureIds: [state.polygon.id],
    });
  }
};

DragCircleMode.onStop = function onStop(state: State) {
  dragPan.enable(this);
  removeLayerIfExists(SOURCE_LINE);
  removeLayerIfExists(SOURCE_SYMBOL);
  removeSourceIfExists(SOURCE_LINE);
  removeSourceIfExists(SOURCE_SYMBOL);
  if (state.polygon.isValid()) {
    this.map.fire('draw.create', {
      features: [state.polygon.toGeoJSON()],
    });
  }
};

DragCircleMode.onClick = function onClick(state: State) {
  // don't draw the circle if its a tap or click event
  state.polygon.properties.center = [];
};

DragCircleMode.onTap = function onTap(state: State) {
  // don't draw the circle if its a tap or click event
  state.polygon.properties.center = [];
};

DragCircleMode.toDisplayFeatures = function toDisplayFeatures(
  state: State,
  geojson,
  display
) {
  const castGeojson = geojson as Feature<
    Polygon,
    { id: string; active: 'true' | 'false' }
  >;
  const isActivePolygon =
    geojson && castGeojson.properties.id === state.polygon.id;
  castGeojson.properties.active = isActivePolygon
    ? constants.activeStates.ACTIVE
    : constants.activeStates.INACTIVE;
  return display(geojson);
};

export default DragCircleMode;
